/* global fnCheckConnection, logError, moment */
import Vue from 'vue'
import { Api } from '@/core/api.js'
// eslint-disable-next-line camelcase
import { db_templates } from '@/database'
import { getFirstCustomRecord } from '@/utils/templateCustom'
// (sin import de flags: permisos se aplican en UI)

const api = new Api()

async function loadTemplateFromStorageById(templateId) {
    if (templateId == null) return { tpl: null, keyUsed: null }

    const keyCandidates = [templateId, String(templateId)]
    const asNumber = Number(templateId)
    if (!Number.isNaN(asNumber)) keyCandidates.push(asNumber)

    const uniqueKeys = [...new Set(keyCandidates)]
    for (const key of uniqueKeys) {
        // eslint-disable-next-line no-await-in-loop
        const raw = await db_templates.getItem(key)
        if (raw == null) continue
        const tpl = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (tpl) return { tpl, keyUsed: key }
    }

    return { tpl: null, keyUsed: null }
}

/**
 * Sustituye la plantilla en IndexedDB por el snapshot del GET checklist/templates/:type
 * (misma fuente que loadTemplates). Así el merge local coincide con sections/items del servidor.
 *
 * @param {string|number} templateId id de plantilla (codificado), como en la ruta de ítems
 * @param {{ commit?: function }} [ctx] si se pasa commit, también actualiza Vuex (updateTemplate)
 * @returns {Promise<boolean>}
 */
async function refreshTemplateInStorageFromApi(templateId, ctx = {}) {
    const { commit } = ctx
    const { tpl, keyUsed } = await loadTemplateFromStorageById(templateId)
    if (!tpl || keyUsed == null) {
        return false
    }
    const rawType = tpl.type != null ? parseInt(tpl.type, 10) : NaN
    if (Number.isNaN(rawType)) {
        return false
    }

    let listResp
    try {
        listResp = await api.get(`checklist/templates/${rawType}`, {})
    } catch (e) {
        logError('refreshTemplateInStorageFromApi: error en GET', e)
        return false
    }

    if (!listResp || listResp.status !== true || listResp.data == null) {
        return false
    }

    const list = Array.isArray(listResp.data)
        ? listResp.data
        : Object.values(listResp.data)
    const idNeedle = String(templateId).trim()
    const fresh = list.find((t) => t && String(t.id).trim() === idNeedle)
    if (!fresh) {
        logError('refreshTemplateInStorageFromApi: plantilla no hallada en lista API', { templateId, rawType })
        return false
    }

    try {
        await db_templates.setItem(keyUsed, JSON.stringify(fresh))
    } catch (e) {
        logError('refreshTemplateInStorageFromApi: error al escribir IndexedDB', e)
        return false
    }

    if (typeof commit === 'function') {
        try {
            commit('updateTemplate', {
                template: fresh,
                type: rawType,
                oldType: null
            })
        } catch (e) {
            // Vuex desfasado no debe romper el flujo; IndexedDB ya está actualizado
        }
    }

    return true
}

/** Plantilla de tareas (checklist_tpl.type === 1). */
function isTaskTemplateType(templateType) {
    const t = parseInt(templateType, 10)
    return !Number.isNaN(t) && t === 1
}

/**
 * Vuelve a pedir GET checklist/templates/1 y persiste en IndexedDB + Vuex.
 * Mantiene al día la definición de ítems usada en el flujo de tareas.
 */
async function reloadTaskTemplatesFromApi(dispatch) {
    try {
        await dispatch('loadTemplates', { type: 1 })
    } catch (e) {
        logError('reloadTaskTemplatesFromApi: error al refrescar plantillas de tareas', e)
    }
}

function normalizeNonEmptyString(value) {
    if (value == null) return ''
    const str = String(value).trim()
    return str
}

function patchItemObject(target, patch) {
    if (!target || typeof target !== 'object') return
    Object.keys(patch).forEach((k) => {
        if (patch[k] === undefined) return
        target[k] = patch[k]
    })
}

function patchFirstMatchingItemInTemplate(tpl, matchFn, patch) {
    if (!tpl || typeof tpl !== 'object') return false
    let changed = false

    const visitItem = (item) => {
        if (!item || typeof item !== 'object') return false
        if (!matchFn(item)) return false
        if (typeof patch === 'function') {
            patch(item)
        } else {
            patchItemObject(item, patch)
        }
        return true
    }

    // Buscar en sections.items (sections puede ser array u objeto)
    if (tpl.sections && typeof tpl.sections === 'object') {
        const rawSections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
        for (const section of rawSections) {
            if (!section || typeof section !== 'object') continue
            const rawItems = section.items && typeof section.items === 'object' ? section.items : null
            if (!rawItems) continue
            const itemList = Array.isArray(rawItems) ? rawItems : Object.values(rawItems)
            for (const item of itemList) {
                if (visitItem(item)) {
                    changed = true
                    return changed
                }
            }
        }
    }

    // Fallback: buscar en tpl.items (puede ser array u objeto)
    if (tpl.items && typeof tpl.items === 'object') {
        const rawItems = tpl.items
        const itemList = Array.isArray(rawItems) ? rawItems : Object.values(rawItems)
        for (const item of itemList) {
            if (visitItem(item)) {
                changed = true
                return changed
            }
        }
    }

    return changed
}

/**
 * Quita de la plantilla el ítem raíz y descendientes (por parent_id).
 * @param {object} tpl
 * @param {string|number} rootItemId id checklist_item (codificado, como en la UI)
 * @returns {boolean}
 */
function removeItemSubtreeFromTemplate(tpl, rootItemId) {
    if (!tpl || rootItemId == null) return false
    const rootStr = String(rootItemId).trim()
    if (rootStr === '') return false

    const itemIdOf = (item) => {
        if (!item || typeof item !== 'object') return null
        if (item.id != null) return String(item.id).trim()
        if (item.item_id != null) return String(item.item_id).trim()
        return null
    }

    const parentIdOf = (item) => {
        if (!item || typeof item !== 'object' || item.parent_id == null) return null
        const s = String(item.parent_id).trim()
        return s === '' ? null : s
    }

    const collectFlat = () => {
        const list = []
        const pushFromContainer = (rawItems) => {
            if (!rawItems || typeof rawItems !== 'object') return
            const arr = Array.isArray(rawItems) ? rawItems : Object.values(rawItems)
            arr.forEach((item) => {
                if (item && typeof item === 'object') list.push(item)
            })
        }
        if (tpl.sections && typeof tpl.sections === 'object') {
            const rawSections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
            rawSections.forEach((section) => {
                if (section && section.items) pushFromContainer(section.items)
            })
        }
        if (tpl.items && typeof tpl.items === 'object') {
            pushFromContainer(tpl.items)
        }
        return list
    }

    const flat = collectFlat()
    const byParent = new Map()
    flat.forEach((item) => {
        const id = itemIdOf(item)
        if (!id) return
        const pid = parentIdOf(item)
        const key = pid == null ? '' : pid
        if (!byParent.has(key)) byParent.set(key, [])
        byParent.get(key).push(id)
    })

    const toRemove = new Set()
    const queue = [rootStr]
    while (queue.length > 0) {
        const id = queue.shift()
        if (toRemove.has(id)) continue
        toRemove.add(id)
        const children = byParent.get(id)
        if (children) {
            children.forEach((cid) => queue.push(cid))
        }
    }

    const filterContainer = (rawItems) => {
        if (!rawItems || typeof rawItems !== 'object') return false
        let changed = false
        if (Array.isArray(rawItems)) {
            for (let i = rawItems.length - 1; i >= 0; i--) {
                const id = itemIdOf(rawItems[i])
                if (id && toRemove.has(id)) {
                    rawItems.splice(i, 1)
                    changed = true
                }
            }
            return changed
        }
        Object.keys(rawItems).forEach((key) => {
            const id = itemIdOf(rawItems[key])
            if (id && toRemove.has(id)) {
                delete rawItems[key]
                changed = true
            }
        })
        return changed
    }

    let changed = false
    if (tpl.sections && typeof tpl.sections === 'object') {
        const rawSections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
        rawSections.forEach((section) => {
            if (section && section.items && filterContainer(section.items)) {
                changed = true
            }
        })
    }
    if (tpl.items && typeof tpl.items === 'object' && filterContainer(tpl.items)) {
        changed = true
    }
    return changed
}

/** Oculta ítems borrados lógicamente si el caché local aún los trae. */
function shouldHideTemplateListItem(item) {
    if (!item || typeof item !== 'object') return false
    const st = Number(item.status)
    if (!Number.isNaN(st) && st === -1) return true
    const rec = getFirstCustomRecord(item.custom)
    if (rec && Number(rec.status) === -1) return true
    return false
}

function patchTemplateItemFromData(item, data) {
    const title = normalizeNonEmptyString(data && data.title)
    const description = normalizeNonEmptyString(data && data.description)
    const refItemId = data && data.ref_item_id !== undefined ? data.ref_item_id : undefined
    const itemId = data && data.item_id !== undefined ? data.item_id : undefined
    const hasLegacyStringCustom = item && typeof item.custom === 'string'
    const isCreatePayload = !!(data && (data.item_id != null || data.created_by != null || data.created_by_employee_id != null))

    const rec = getFirstCustomRecord(item.custom)
    if (rec) {
        rec.title = title
        rec.description = description
        if (data && data.equipment_id !== undefined) rec.equipment_id = data.equipment_id
        if (data && data.apply !== undefined) rec.apply = data.apply
        if (refItemId !== undefined) rec.ref_item_id = refItemId
        if (itemId !== undefined) rec.item_id = itemId
    } else {
        // Importante: NO usar ref_item_id como señal de creación.
        // Ahora también se envía en updates, y eso haría que items con custom legacy (string)
        // se conviertan erróneamente en "custom local" con created_by='local'.
        if (isCreatePayload && !hasLegacyStringCustom) {
            const createdBy = data && data.created_by != null ? String(data.created_by).trim() : 'local'
            const createdByEmployeeId = data && (data.created_by_employee_id != null ? data.created_by_employee_id : data.employee_id)
            item.custom = [
                {
                    title,
                    description,
                    ref_item_id: refItemId !== undefined ? refItemId : null,
                    item_id: itemId !== undefined
                        ? itemId
                        : (item && (item.id != null ? item.id : item.item_id != null ? item.item_id : null)),
                    equipment_id: data && data.equipment_id !== undefined ? data.equipment_id : null,
                    apply: data && data.apply !== undefined ? data.apply : 1,
                    created_by: createdBy,
                    created_by_employee_id: createdByEmployeeId != null ? String(createdByEmployeeId).trim() : null
                }
            ]
            item.description = ''
        } else {
            item.custom = title
            item.description = description
        }
    }

    if (data && data.type !== undefined) item.type = data.type
    if (data && data.apply !== undefined) item.apply = data.apply
    if (data && data.visible !== undefined) item.visible = data.visible
    if (data && data.equipment_id !== undefined) item.equipment_id = data.equipment_id
}

const BASE_ITEMS_CACHE_KEY = 'andy_base_items_cache'

function loadBaseItemsFromCache() {
    try {
        const cached = localStorage.getItem(BASE_ITEMS_CACHE_KEY)
        if (cached) {
            const parsed = JSON.parse(cached)
            if (Array.isArray(parsed) && parsed.length > 0) return parsed
        }
    } catch (e) {}
    return []
}

function saveBaseItemsToCache(items) {
    try {
        localStorage.setItem(BASE_ITEMS_CACHE_KEY, JSON.stringify(items))
    } catch (e) {}
}

const BASE_ITEMS_TYPE6_CACHE_KEY = 'andy_base_items_type6_cache'

function loadBaseItemsType6FromCache() {
    try {
        const cached = localStorage.getItem(BASE_ITEMS_TYPE6_CACHE_KEY)
        if (cached) {
            const parsed = JSON.parse(cached)
            if (Array.isArray(parsed) && parsed.length > 0) return parsed
        }
    } catch (e) {}
    return []
}

function saveBaseItemsType6ToCache(items) {
    try {
        localStorage.setItem(BASE_ITEMS_TYPE6_CACHE_KEY, JSON.stringify(items))
    } catch (e) {}
}

// Función para resetear el state
const getDefaultState = () => {
    return {
        templates: {
            1: {}, // Tasks
            2: {}, // Records
            3: {}, // Audits
            4: {}, // Registers
            5: {}, // Action Plans
            6: {} // Customizables
        },
        baseItems: loadBaseItemsFromCache(),
        baseItemsType6: loadBaseItemsType6FromCache(),
        loading: false,
        error: null,
        lastSync: null
    }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
    getAllTemplates: (state, getters, rootState) => {
        return state.templates
    },
    getBaseItems: (state) => {
        return state.baseItems || []
    },
    getBaseItemsType6: (state) => {
        return state.baseItemsType6 || []
    },
    getTemplatesByType: (state, getters, rootState) => (type) => {
        return state.templates[type] || {}
    },
    getTemplateById: (state, getters, rootState) => (type, id) => {
        if (state.templates[type] && state.templates[type][id]) {
            return state.templates[type][id]
        }
        return false
    },
    isLoading: (state, getters, rootState) => {
        return state.loading
    },
    getError: (state, getters, rootState) => {
        return state.error
    },
    getLastSync: (state, getters, rootState) => {
        return state.lastSync
    }
}

// Singleton promise para loadAllBaseItems: evita iteración concurrente sobre IndexedDB
let _loadAllBaseItemsPromise = null
let _loadBaseItemsType6Promise = null

// actions
const actions = {
    // Resetear el estado del módulo
    resetState({ commit }) {
        commit('resetState')
    },

    // Cargar templates desde la API o desde almacenamiento local
    async loadTemplates({ commit, dispatch }, params = {}) {
        const type = params.type || 1

        if (!fnCheckConnection()) {
            return dispatch('loadTemplatesOffline', { type })
        }

        commit('setLoading', true)
        commit('setError', null)

        try {
            const response = await api.get(`checklist/templates/${type}`, {})

            if (response && response.data) {
                commit('setTemplates', { data: response.data, type })

                // Guardar en base de datos local
                // Si falla la guardada en DB, no sobrescribir los datos frescos ya guardados en Vuex
                try {
                    await Promise.all(
                        Object.values(response.data).map(async(template) => {
                            await db_templates.setItem(template.id, JSON.stringify(template))
                        })
                    )
                } catch (dbError) {
                    // Error al guardar en DB, pero los datos ya están en Vuex
                    // Registrar el error pero no sobrescribir con datos obsoletos
                    logError('Error al guardar templates en base de datos local:', dbError)
                    commit('setError', dbError)
                    // No llamar a loadTemplatesOffline para evitar sobrescribir datos frescos
                    commit('setLoading', false)
                    return response.data
                }

                commit('setLastSync', new Date().toISOString())
                commit('setLoading', false)
                return response.data
            } else {
                // Si la respuesta es exitosa pero sin datos, intentar cargar desde almacenamiento local
                // No resetear loading aquí, loadTemplatesOffline lo manejará
                return dispatch('loadTemplatesOffline', { type })
            }
        } catch (error) {
            logError(error)
            commit('setError', error)
            // Intentar cargar desde almacenamiento local solo si es un error de API
            // No resetear loading aquí, loadTemplatesOffline lo manejará
            return dispatch('loadTemplatesOffline', { type })
        }
    },

    // Cargar templates desde almacenamiento local
    async loadTemplatesOffline({ commit }, params = {}) {
        const type = params.type || 1
        const templates = {}

        commit('setLoading', true)
        commit('setError', null)

        try {
            await db_templates.iterate((value, key) => {
                const template = typeof value === 'string' ? JSON.parse(value) : value
                // Usar parseInt para comparar tipos ya que la API puede devolver strings
                if (parseInt(template.type) === parseInt(type)) {
                    templates[template.id] = template
                }
            })

            if (Object.keys(templates).length > 0) {
                commit('setTemplates', { data: templates, type })
            }

            commit('setLoading', false)
            return templates
        } catch (error) {
            logError(error)
            commit('setError', error)
            commit('setLoading', false)
            return {}
        }
    },

    // Crear un nuevo template
    async createTemplate({ commit, dispatch }, params) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        commit('setLoading', true)
        commit('setError', null)

        try {
            const response = await api.post('checklist/templates', params)

            if (response && response.data) {
                const template = response.data
                commit('addTemplate', { template, type: template.type })

                // Guardar en base de datos local
                await db_templates.setItem(template.id, JSON.stringify(template))

                return template
            } else {
                throw new Error('La respuesta de la API no contiene datos válidos')
            }
        } catch (error) {
            logError(error)
            commit('setError', error)
            throw error
        } finally {
            commit('setLoading', false)
        }
    },

    // Actualizar un template existente
    async updateTemplate({ commit, getters }, params) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        commit('setLoading', true)
        commit('setError', null)

        try {
            const response = await api.put(`checklist/templates/${params.id}`, params.data)

            if (response && response.data) {
                const template = response.data

                // Buscar el template en el estado para encontrar su tipo anterior si existe
                let oldType = null
                for (const type of [1, 2, 3, 4, 5]) {
                    const existingTemplate = getters.getTemplateById(type, template.id)
                    if (existingTemplate) {
                        oldType = type
                        break
                    }
                }

                // Si el tipo cambió, eliminar del tipo anterior y agregar al nuevo
                // Usar parseInt para comparar tipos ya que la API puede devolver strings
                if (oldType !== null && parseInt(oldType) !== parseInt(template.type)) {
                    commit('removeTemplate', { id: template.id, type: oldType })
                    commit('addTemplate', { template, type: template.type })
                } else {
                    // Si el tipo no cambió o es nuevo, usar la mutación normal de actualización
                    commit('updateTemplate', { template, type: template.type, oldType })
                }

                // Actualizar en base de datos local
                await db_templates.setItem(template.id, JSON.stringify(template))

                return template
            } else {
                throw new Error('La respuesta de la API no contiene datos válidos')
            }
        } catch (error) {
            logError(error)
            commit('setError', error)
            throw error
        } finally {
            commit('setLoading', false)
        }
    },

    // Eliminar un template
    async deleteTemplate({ commit, getters }, params) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        if (!params.id) {
            throw new Error('El ID del template es requerido')
        }

        // Determinar el tipo del template si no se proporciona
        let templateType = params.type
        if (!templateType) {
            // Buscar el template en el estado para obtener su tipo
            for (const type of [1, 2, 3, 4, 5]) {
                const template = getters.getTemplateById(type, params.id)
                if (template) {
                    templateType = type
                    break
                }
            }
        }

        if (!templateType) {
            throw new Error('No se pudo determinar el tipo del template. Por favor, proporcione el parámetro type.')
        }

        // Verificar que el template existe antes de intentar eliminarlo
        const templateExists = getters.getTemplateById(templateType, params.id)
        if (!templateExists) {
            throw new Error(`El template con ID ${params.id} no existe en el estado`)
        }

        commit('setLoading', true)
        commit('setError', null)

        try {
            await api.delete(`checklist/templates/${params.id}`, {})

            commit('removeTemplate', { id: params.id, type: templateType })

            // Verificar que la mutación realmente eliminó el template
            const stillExists = getters.getTemplateById(templateType, params.id)
            if (stillExists) {
                throw new Error('Error al eliminar el template del estado de Vuex')
            }

            // Eliminar de base de datos local
            await db_templates.removeItem(params.id)

            return true
        } catch (error) {
            logError(error)
            commit('setError', error)
            throw error
        } finally {
            commit('setLoading', false)
        }
    },

    // Sincronizar todos los templates
    async syncAllTemplates({ dispatch }) {
        const types = [1, 2, 3, 4, 5]
        const results = {}

        for (const type of types) {
            try {
                results[type] = await dispatch('loadTemplates', { type })
            } catch (error) {
                logError(`Error sincronizando templates tipo ${type}:`, error)
                results[type] = null
            }
        }

        return results
    },

    // Listar plantillas desde storage (sin endpoint)
    // params.type: 'tasks' | 'records'
    // params.search: string opcional
    async loadCustomizableTemplates({ commit, dispatch, getters }, params = {}) {
        commit('setError', null)

        const normalizeText = (text) => {
            if (text == null) return ''
            return String(text)
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
        }

        // En el backend: 1=tareas, 2=inspecciones/audits, 3=registros.
        const TOOL_TO_TYPES = {
            tasks: [1],
            records: [3]
        }

        const types = TOOL_TO_TYPES[params.type] || []
        if (types.length === 0) return []

        try {
            // Cargar desde DB local al estado (sin pegar a API)
            await Promise.all(types.map((type) => dispatch('loadTemplatesOffline', { type })))

            // Unificar y deduplicar por id
            const merged = []
            const seen = new Set()
            types.forEach((type) => {
                const bucket = getters.getTemplatesByType(type) || {}
                Object.values(bucket).forEach((tpl) => {
                    if (!tpl || tpl.id == null) return
                    const id = String(tpl.id)
                    if (seen.has(id)) return
                    seen.add(id)
                    merged.push(tpl)
                })
            })

            const search = normalizeText(params.search)
            if (!search) return merged

            const parts = search.split(/\s+/).filter(Boolean)
            return merged
                .map((tpl) => ({
                    tpl,
                    score: parts.reduce((acc, word) => {
                        const hay = normalizeText(tpl.name || tpl.template_name || '')
                        return hay.includes(word) ? acc + 1 + word.length * 0.1 : acc
                    }, 0)
                }))
                .filter((x) => x.score > 0)
                .sort((a, b) => (a.score < b.score ? 1 : -1))
                .map((x) => x.tpl)
        } catch (error) {
            logError('Error cargando plantillas desde storage:', error)
            commit('setError', error)
            return []
        }
    },

    // Obtener items de una plantilla
    async loadTemplateItems({ commit }, { templateId } = {}) {
        if (!templateId) {
            logError('templateId es requerido para cargar items desde storage')
            return null
        }

        commit('setError', null)

        const pickSectionName = (section) => {
            if (!section) return ''
            return section.title || section.name || section.label || ''
        }

        const toItem = (rawItem) => {
            const item = typeof rawItem === 'object' && rawItem ? { ...rawItem } : {}
            const id = item.id != null ? item.id : (item.item_id != null ? item.item_id : undefined)
            return {
                ...item,
                id,
                title: item.title || item.name || item.label || '',
                description: item.description || '',
                apply: item.apply != null ? item.apply : 1
            }
        }

        const buildSections = (tpl) => {
            // Nota: los permisos de edición/creación se aplican en el formulario (TemplateItemEdit.vue)
            // usando template.config.edit_items/create_items. Evitar duplicar estado en cada item.

            // Caso 1: tpl.sections existe (objeto o array) y ya viene estructurado
            if (tpl && tpl.sections && typeof tpl.sections === 'object') {
                const rawSections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)

                const sectionsFromSections = rawSections
                    .filter(Boolean)
                    .map((section) => {
                        const sectionId = section.section_id != null ? section.section_id : (section.id != null ? section.id : undefined)

                        const rawItems = section.items && typeof section.items === 'object' ? section.items : {}
                        const itemList = Array.isArray(rawItems) ? rawItems : Object.values(rawItems || {})
                        const items = itemList
                            .filter(Boolean)
                            .filter((it) => !shouldHideTemplateListItem(it))
                            .map((it) => toItem(it))
                            .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))

                        return {
                            section_id: sectionId != null ? sectionId : String(pickSectionName(section) || ''),
                            name: pickSectionName(section),
                            items
                        }
                    })
                    .filter((s) => Array.isArray(s.items) && s.items.length > 0)

                // Si sections existe pero está vacío / no tiene items, caer al fallback por tpl.items
                if (sectionsFromSections.length > 0) return sectionsFromSections
            }

            // Caso 2: fallback: armar secciones por item tipo 8 (título/sección)
            const rawItems = tpl && tpl.items ? tpl.items : {}
            const list = Array.isArray(rawItems) ? rawItems : Object.values(rawItems || {})
            const sorted = list
                .filter(Boolean)
                .filter((it) => !shouldHideTemplateListItem(it))
                .map((it) => toItem(it))
                .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))

            const sections = []
            let current = { section_id: 'default', name: '', items: [] }

            sorted.forEach((item) => {
                if (Number(item.type) === 8) {
                    if (current.items.length > 0) sections.push(current)
                    current = {
                        section_id: item.id != null ? item.id : `section-${sections.length + 1}`,
                        name: item.title || '',
                        items: []
                    }
                    return
                }
                current.items.push(item)
            })
            if (current.items.length > 0) sections.push(current)
            return sections
        }

        try {
            const { tpl } = await loadTemplateFromStorageById(templateId)
            if (!tpl) return null

            const rawTplType = tpl.type != null ? parseInt(tpl.type, 10) : NaN

            return {
                template_name: tpl.name || tpl.template_name || '',
                template_type: Number.isNaN(rawTplType) ? null : rawTplType,
                config: tpl.config || {},
                sections: buildSections(tpl)
            }
        } catch (error) {
            logError('Error cargando items desde storage:', error)
            commit('setError', error)
            return null
        }
    },

    // Cargar todos los ítems base de todas las plantillas y guardar en Vuex state
    async loadAllBaseItems({ commit, state }) {
        // Guard: si ya hay una carga en curso, reusar el mismo promise para evitar
        // doble iteración concurrente sobre db_templates (que provoca bloqueo de IndexedDB).
        if (_loadAllBaseItemsPromise) {
            return _loadAllBaseItemsPromise
        }

        const allItems = []
        const seenIds = new Set()

        const extractItems = (rawItems) => {
            const list = Array.isArray(rawItems) ? rawItems : Object.values(rawItems || {})
            list.forEach((item) => {
                if (!item) return
                const id = item.id != null ? item.id : item.item_id
                if (id == null || seenIds.has(String(id))) return
                if (Number(item.type) === 8) return
                seenIds.add(String(id))
                allItems.push({
                    id,
                    title: item.title || item.name || item.label || '',
                    description: item.description || '',
                    type: item.type
                })
            })
        }

        const extractFromTemplate = (tpl) => {
            if (!tpl) return
            let foundInSections = false
            if (tpl.sections && typeof tpl.sections === 'object') {
                const sections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
                sections.forEach((section) => {
                    if (section && section.items) {
                        extractItems(section.items)
                        foundInSections = true
                    }
                })
            }
            if (!foundInSections && tpl.items) {
                extractItems(tpl.items)
            }
        }

        _loadAllBaseItemsPromise = (async () => {
            try {
                await db_templates.iterate((value) => {
                    const tpl = typeof value === 'string' ? JSON.parse(value) : value
                    extractFromTemplate(tpl)
                })
            } catch (error) {
                logError('Error cargando todos los items base:', error)
                commit('setError', error)
            } finally {
                _loadAllBaseItemsPromise = null
            }

            commit('setBaseItems', allItems)
            saveBaseItemsToCache(allItems)
            return allItems
        })()

        return _loadAllBaseItemsPromise
    },

    async loadBaseItemsFromType6Templates({ commit, dispatch }) {
        if (_loadBaseItemsType6Promise) {
            return _loadBaseItemsType6Promise
        }

        const allItems = []
        const seenIds = new Set()

        const extractItems = (rawItems) => {
            const list = Array.isArray(rawItems) ? rawItems : Object.values(rawItems || {})
            list.forEach((item) => {
                if (!item) return
                if (item.parent_id != null) return
                const id = item.id != null ? item.id : item.item_id
                if (id == null || seenIds.has(String(id))) return
                if (Number(item.type) === 8) return
                seenIds.add(String(id))
                allItems.push({
                    id,
                    title: item.title || item.name || item.label || '',
                    description: item.description || '',
                    type: item.type
                })
            })
        }

        const extractFromTemplate = (tpl) => {
            if (!tpl) return
            let foundInSections = false
            if (tpl.sections && typeof tpl.sections === 'object') {
                const sections = Array.isArray(tpl.sections) ? tpl.sections : Object.values(tpl.sections)
                sections.forEach((section) => {
                    if (section && section.items) {
                        extractItems(section.items)
                        foundInSections = true
                    }
                })
            }
            if (!foundInSections && tpl.items) {
                extractItems(tpl.items)
            }
        }

        _loadBaseItemsType6Promise = (async () => {
            try {
                // 1) Cargar plantillas tipo 6 desde API (online) o IndexedDB (offline); si falla, seguimos con lo que haya en DB
                try {
                    if (fnCheckConnection()) {
                        await dispatch('loadTemplates', { type: 6 })
                    } else {
                        await dispatch('loadTemplatesOffline', { type: 6 })
                    }
                } catch (loadErr) {
                    logError('Error cargando plantillas tipo 6 (se usará cache/IndexedDB):', loadErr)
                }

                // 2) Iterar IndexedDB y extraer ítems solo de plantillas con type === 6
                await db_templates.iterate((value) => {
                    const tpl = typeof value === 'string' ? JSON.parse(value) : value
                    if (tpl && parseInt(tpl.type) === 6) {
                        extractFromTemplate(tpl)
                    }
                })

                commit('setBaseItemsType6', allItems)
                saveBaseItemsType6ToCache(allItems)
                return allItems
            } catch (error) {
                logError('Error cargando ítems base de plantillas tipo 6:', error)
                commit('setError', error)
                commit('setBaseItemsType6', [])
                saveBaseItemsType6ToCache([])
                return []
            } finally {
                _loadBaseItemsType6Promise = null
            }
        })()

        return _loadBaseItemsType6Promise
    },

    // Eliminar item personalizado
    async deleteTemplateItem({ commit, dispatch }, { templateId, itemId, templateType }) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        try {
            const response = await api.delete(`checklist/templates/${templateId}/items/${itemId}`)
            const ok = response && response.status === true
            if (ok) {
                if (isTaskTemplateType(templateType)) {
                    await reloadTaskTemplatesFromApi(dispatch)
                } else {
                    const refreshed = await refreshTemplateInStorageFromApi(templateId, { commit })
                    if (!refreshed) {
                        try {
                            const { tpl, keyUsed } = await loadTemplateFromStorageById(templateId)
                            if (tpl && keyUsed != null && removeItemSubtreeFromTemplate(tpl, itemId)) {
                                await db_templates.setItem(keyUsed, JSON.stringify(tpl))
                            }
                        } catch (e) {
                            // Si falla el ajuste local, no romper el flujo principal (el server ya eliminó)
                        }
                    }
                }
            }
            return ok
        } catch (error) {
            logError('Error eliminando item de plantilla:', error)
            commit('setError', error)
            return false
        }
    },

    // Actualizar visibilidad (apply) de item
    async updateTemplateItemVisibility({ commit, dispatch }, { templateId, itemId, apply, templateType }) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        try {
            const response = await api.patch(`checklist/templates/${templateId}/items/${itemId}/visibility`, { apply })
            const ok = response && response.status === true
            if (ok) {
                if (isTaskTemplateType(templateType)) {
                    await reloadTaskTemplatesFromApi(dispatch)
                } else {
                    try {
                        const { tpl, keyUsed } = await loadTemplateFromStorageById(templateId)
                        if (tpl && keyUsed != null) {
                            const idStr = String(itemId)
                            const applyNum = Number(apply)
                            const safeApply = Number.isNaN(applyNum) ? 1 : applyNum
                            patchFirstMatchingItemInTemplate(
                                tpl,
                                (it) => String(it.id != null ? it.id : it.item_id) === idStr,
                                (item) => {
                                    const rec = getFirstCustomRecord(item.custom)
                                    if (rec) rec.apply = safeApply
                                    item.apply = safeApply
                                    item.visible = safeApply === 1
                                }
                            )
                            await db_templates.setItem(keyUsed, JSON.stringify(tpl))
                        }
                    } catch (e) {
                        // Si falla el patch local, no romper el flujo principal (el server ya actualizó)
                    }
                }
            }
            return ok
        } catch (error) {
            logError('Error actualizando visibilidad de item:', error)
            commit('setError', error)
            return false
        }
    },

    // Crear nuevo item personalizado en una plantilla (POST)
    async createTemplateItem({ commit, dispatch }, { templateId, data, templateType }) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        try {
            const response = await api.post(`checklist/templates/${templateId}/items`, data)
            const ok = response && response.status === true
            if (ok) {
                if (isTaskTemplateType(templateType)) {
                    await reloadTaskTemplatesFromApi(dispatch)
                } else {
                    const refreshed = await refreshTemplateInStorageFromApi(templateId, { commit })
                    if (!refreshed) {
                        logError(
                            'createTemplateItem: no se pudo refrescar la plantilla desde la API; el listado local puede quedar desactualizado hasta la próxima sincronización',
                            { templateId }
                        )
                    }
                }
            }
            return ok
        } catch (error) {
            logError('Error creando item de plantilla:', error)
            commit('setError', error)
            return false
        }
    },

    // Actualizar item existente de plantilla (PUT)
    async updateTemplateItem({ commit, dispatch }, { templateId, itemId, data, templateType }) {
        if (!fnCheckConnection()) {
            throw new Error('No hay conexión disponible')
        }

        try {
            const response = await api.put(`checklist/templates/${templateId}/items/${itemId}`, data)
            const ok = response && response.status === true
            if (ok) {
                if (isTaskTemplateType(templateType)) {
                    await reloadTaskTemplatesFromApi(dispatch)
                } else {
                    try {
                        const { tpl, keyUsed } = await loadTemplateFromStorageById(templateId)
                        if (tpl && keyUsed != null) {
                            const idStr = String(itemId)
                            patchFirstMatchingItemInTemplate(
                                tpl,
                                (it) => String(it.id != null ? it.id : it.item_id) === idStr,
                                (item) => {
                                    patchTemplateItemFromData(item, data)
                                }
                            )
                            await db_templates.setItem(keyUsed, JSON.stringify(tpl))
                        }
                    } catch (e) {
                        // Si falla el patch local, no romper el flujo principal (el server ya actualizó)
                    }
                }
            }
            return ok
        } catch (error) {
            logError('Error actualizando item de plantilla:', error)
            commit('setError', error)
            return false
        }
    }
}

// mutations
const mutations = {
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setLoading(state, value) {
        state.loading = value
    },
    setBaseItems(state, items) {
        Vue.set(state, 'baseItems', Array.isArray(items) ? items : [])
    },
    setBaseItemsType6(state, items) {
        Vue.set(state, 'baseItemsType6', Array.isArray(items) ? items : [])
    },
    setError(state, error) {
        state.error = error
    },
    setLastSync(state, timestamp) {
        state.lastSync = timestamp
    },
    setTemplates(state, payload) {
        const { data, type } = payload
        const templates = { ...state.templates }

        // Reemplazar completamente el contenido del tipo para eliminar templates que ya no existen en el servidor
        templates[type] = {}

        Object.keys(data).forEach((templateId) => {
            const template = { ...data[templateId] }

            // Formatear fecha si existe
            if (template.created_date) {
                template.created_date = template.created_date !== false
                    ? moment(template.created_date)
                    : moment()
            } else {
                template.created_date = moment()
            }

            templates[type][template.id] = template
        })

        Vue.set(state, 'templates', { ...templates })
    },
    addTemplate(state, payload) {
        const { template, type } = payload
        const templates = { ...state.templates }

        if (!templates[type]) {
            templates[type] = {}
        }

        const formattedTemplate = { ...template }
        if (formattedTemplate.created_date) {
            formattedTemplate.created_date = formattedTemplate.created_date !== false
                ? moment(formattedTemplate.created_date)
                : moment()
        } else {
            formattedTemplate.created_date = moment()
        }

        templates[type][template.id] = formattedTemplate
        Vue.set(state, 'templates', { ...templates })
    },
    updateTemplate(state, payload) {
        const { template, type, oldType } = payload
        const templates = { ...state.templates }

        // Si se proporciona oldType (no null ni undefined) y es diferente del nuevo tipo,
        // ya fue manejado por la acción (removeTemplate + addTemplate)
        // Solo procesar si el template existe en el tipo actual o si oldType es null/undefined
        if (oldType !== null && oldType !== undefined && oldType !== type) {
            // Ya fue manejado por la acción (removeTemplate + addTemplate)
            return
        }

        // Si el template existe en el tipo actual, actualizarlo
        if (templates[type] && templates[type][template.id]) {
            const updatedTemplate = { ...templates[type][template.id], ...template }

            if (updatedTemplate.created_date) {
                updatedTemplate.created_date = updatedTemplate.created_date !== false
                    ? moment(updatedTemplate.created_date)
                    : moment()
            }

            templates[type][template.id] = updatedTemplate
            Vue.set(state, 'templates', { ...templates })
        } else {
            // Si no existe en el tipo actual, agregarlo (puede ser un template nuevo o movido)
            if (!templates[type]) {
                templates[type] = {}
            }

            const formattedTemplate = { ...template }
            if (formattedTemplate.created_date) {
                formattedTemplate.created_date = formattedTemplate.created_date !== false
                    ? moment(formattedTemplate.created_date)
                    : moment()
            } else {
                formattedTemplate.created_date = moment()
            }

            templates[type][template.id] = formattedTemplate
            Vue.set(state, 'templates', { ...templates })
        }
    },
    removeTemplate(state, payload) {
        const { id, type } = payload
        const templates = { ...state.templates }

        if (templates[type] && templates[type][id]) {
            delete templates[type][id]
            Vue.set(state, 'templates', { ...templates })
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
